import * as RadixDropdownMenu from '@radix-ui/react-dropdown-menu';
import { isFunction } from 'lodash-es';
import type { KeyboardEvent, MouseEvent, ReactNode } from 'react';
import { Children, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { Interpolation } from 'styled-components';

import type { ButtonSize, ButtonVariant } from 'components/ui/shared/Buttons/Button.styles';
import type { ItemViewType } from 'enums/ItemViewType';

import { ChevronDownIcon, ChevronUpIcon, MoreIcon, MoreVerticalIcon } from 'components/ui/icons';
import { MenuContext } from 'components/ui/menus/menuContext';
import Button from 'components/ui/shared/Buttons/Button';
import IconButton from 'components/ui/shared/Buttons/IconButton';
import { ElementTestId } from 'enums/testing';

import {
  ClosedMenuItemsContainer,
  Container,
  CustomTrigger,
  MenuIconContainer,
  MenuItemsContainer,
} from './MenuButton.styles';

export { default as MenuItem } from './MenuButtonItem';
export type { MenuItemConfig } from './MenuItems';
export { default as MenuItems } from './MenuItems';

export type MenuButtonType = 'custom' | 'default' | 'more' | 'more-vertical';

export type MenuPosition = 'bottom-left' | 'top-left' | 'top-right';

/**
 * The event Radix hands to `onInteractOutside`, for either a press or a focus move outside the
 * dropdown. Derived from the content's own props so it tracks Radix rather than pinning a type from
 * `react-dismissable-layer`, which is a transitive dependency.
 */
type InteractOutsideEvent = Parameters<NonNullable<RadixDropdownMenu.DropdownMenuContentProps['onInteractOutside']>>[0];

/**
 * Gap (px) between the trigger and the dropdown, and the viewport padding collision handling keeps.
 */
const MENU_OFFSET = 4;

/**
 * The gap `bottom-left` needs instead. It is the global nav's position, where the trigger sits inside
 * the rail's own `SPACE_300` padding, so the gap has to cover that padding as well or the dropdown
 * opens on top of the rail rather than beside it. 12px of rail padding plus an 8px gap of its own,
 * which is wider than `MENU_OFFSET` because the rail is a much heavier edge to clear.
 */
const NAV_RAIL_MENU_OFFSET = 20;

const MENU_PLACEMENT: Record<
  MenuPosition,
  { align: 'center' | 'end' | 'start'; side: 'bottom' | 'left' | 'right' | 'top'; sideOffset: number }
> = {
  'bottom-left': { align: 'end', side: 'right', sideOffset: NAV_RAIL_MENU_OFFSET },
  'top-left': { align: 'start', side: 'bottom', sideOffset: MENU_OFFSET },
  'top-right': { align: 'end', side: 'bottom', sideOffset: MENU_OFFSET },
};

export interface MenuItemProps {
  /**
   * Whether on not the details view for the item is updating
   */
  isUpdating: boolean;
  /**
   * The item being render in the details view
   */
  item?: any;
  /**
   * Callback for when edit mode is toggled
   */
  onToggleEditMode?: (event: MouseEvent<HTMLButtonElement, MouseEvent>, active: boolean) => Promise<void>;
  /**
   * Callback for when an item is archived
   */
  onUpdateArchiveStatus?: () => void;
  /**
   * Sets the `isDetailsUpdated` state
   */
  setIsDetailsUpdating: (isUpdating: boolean) => void;
  /**
   * The item view type
   */
  type: ItemViewType;
}

interface Props {
  /**
   * Accessible name for the trigger, required when it renders as an icon with no visible text
   */
  ariaLabel?: string;
  /**
   * Whether the menu should automatically close when an item is clicked
   */
  autoClose?: boolean;
  /**
   * The `size` variant forwarded to the underlying `Button` or `IconButton`
   */
  buttonSize?: ButtonSize;
  /**
   * Visual variant forwarded to the trigger button (or icon button for "more").
   */
  buttonVariant?: ButtonVariant;
  /**
   * Menu items to render inside the dropdown. Once open the dropdown is a Radix menu, which swallows
   * `Tab` and routes every printable key to item typeahead, so a child that needs either — a text
   * input, say — cannot be driven by the keyboard in here.
   */
  children?: ReactNode;
  /**
   * Custom icon that can be either a function receiving isOpen state or a ReactNode
   */
  customIcon?: ((isOpen: boolean) => ReactNode) | ReactNode;
  /**
   * Whether the menu button is disabled
   */
  disabled?: boolean;
  /**
   * CSS override forwarded to the internal IconButton when `type` is `"more"` or `"more-vertical"`.
   */
  iconButtonCss?: Interpolation<object>;
  /**
   * Whether the dropdown should match the trigger width instead of sizing to its contents
   */
  matchTriggerWidth?: boolean;
  /**
   * When true, removes the default z-index from the container
   */
  noZIndex?: boolean;
  /**
   * Fires on every open and every close, whatever caused it — a trigger press, a keyboard open, an
   * item being selected, Escape, or a click outside. Suits best-effort work such as warming a cache
   * the menu reads; not work that must run exactly once per press.
   */
  onClick?: () => void;
  /**
   * When true, renders the dropdown in a portal, so it escapes any `overflow: hidden` or stacking
   * context ancestor (e.g. a settings list card). Defaults to rendering inline next to the trigger.
   */
  portal?: boolean;
  /**
   * Position where the dropdown menu should appear relative to the button
   */
  position?: MenuPosition;
  /**
   * Test ID for the menu button container
   */
  testId?: string;
  /**
   * Text to display in the button
   */
  text?: string;
  /**
   * Visual style type of the menu button
   */
  type?: MenuButtonType;
}

const MenuButton = ({
  ariaLabel,
  autoClose = true,
  buttonSize,
  buttonVariant,
  customIcon = null,
  disabled = false,
  iconButtonCss,
  matchTriggerWidth = false,
  noZIndex,
  onClick,
  portal = false,
  position = 'top-left',
  testId,
  text = '',
  type = 'default',
  ...props
}: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  /*
   * The dropdown mounts on first open and stays mounted, hidden, from then on. Unmounting it on close
   * would discard state owned by the children -- a menu item that opens a dialog sets that state and
   * closes the menu in the same commit, taking the dialog with the subtree. Twelve `*MenuItems`
   * components render their own confirm dialog next to their items, so this is the common case, not
   * an edge one, and a delayed unmount tears the dialog down a moment after it appears.
   */
  const [hasOpened, setHasOpened] = useState(false);
  const [menuItemsCount, setMenuItemsCount] = useState<number | undefined>(undefined);
  /*
   * Resolved once the menu is actually about to need it, not on mount -- the force-mounted content
   * re-renders continuously once open, so this must not re-query on every one of those renders, but a
   * lookup at mount time could run before `modal-root` exists in an embedding that injects it later.
   */
  const portalContainer = useMemo(
    () => (hasOpened ? (document.getElementById('modal-root') ?? undefined) : undefined),
    [hasOpened]
  );
  const menuItemsRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const hasInteractedOutsideRef = useRef(false);
  /*
   * Whether the press that is closing the menu came from a pointer inside the dropdown. Cleared on a
   * key press so a press that did not close the menu -- an `autoClose={false}` toggle, say -- cannot
   * leave the flag set for a later keyboard close.
   */
  const hasPointerPressInsideRef = useRef(false);
  /*
   * Mirrors `isOpen` for the close paths that read it inside the event that changes it, where the
   * state value is still the pre-commit one. Radix's own dismiss and the dropdown's press handler both
   * fire for a single item press, and the ref is what makes the second one a no-op.
   */
  const isOpenRef = useRef(false);
  /*
   * Whether Radix has already done its one mount-time focus move, after which reopening is this
   * component's job.
   */
  const hasFocusedOnOpenRef = useRef(false);

  /*
   * The dropdown stays mounted once opened, so Radix's own focus scope never unmounts to hand focus
   * back to the trigger -- do it here instead, for the keyboard closes that need it: after Escape or
   * activating an item with Enter, focus would otherwise be stranded on a hidden row.
   *
   * Two closes are left alone. A press elsewhere on the page has already moved focus to whatever the
   * user clicked. And a pointer press on an item must not move focus either: an item that opens a
   * dialog does so in the same commit as the close, and none of this app's dialogs take focus for
   * themselves, so claiming it here leaves the user typing into a menu trigger they cannot see.
   */
  const returnFocusToTrigger = useCallback(() => {
    if (!hasInteractedOutsideRef.current && !hasPointerPressInsideRef.current) {
      triggerRef.current?.focus();
    }

    hasInteractedOutsideRef.current = false;
    hasPointerPressInsideRef.current = false;
  }, []);

  /*
   * The dropdown stays mounted while closed, so its dismissable layer keeps listening -- and the
   * trigger sits outside it. Left alone, the same press that toggles the trigger open is also read as
   * a press outside and dismisses the menu in the same commit, so it never reopens after its first
   * close. Preventing the event drops Radix's dismiss and leaves the trigger's own toggle as the one
   * thing driving open state. A focus move back onto the trigger is skipped for the same reason.
   *
   * That same listener also runs while the menu is closed, where it is a no-op: bailing out before
   * recording anything keeps a stray outside press while closed from being misread as one that
   * happened during the next open.
   */
  const handleInteractOutside = useCallback((event: InteractOutsideEvent) => {
    if (!isOpenRef.current) {
      return;
    }

    /*
     * The press that opens the menu swaps the trigger's chevron before this listener runs, so the target
     * can be an icon already detached from the trigger. The event path is fixed at dispatch and still
     * lists the trigger.
     */
    const trigger = triggerRef.current;

    if (trigger && event.detail.originalEvent.composedPath().includes(trigger)) {
      event.preventDefault();

      return;
    }

    hasInteractedOutsideRef.current = true;
  }, []);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (open === isOpenRef.current) {
        return;
      }

      isOpenRef.current = open;
      setIsOpen(open);

      if (open) {
        setHasOpened(true);
      } else {
        returnFocusToTrigger();
      }

      onClick?.();
    },
    [onClick, returnFocusToTrigger]
  );

  /*
   * Callers place plain buttons and toggles inside the dropdown, so a press on any child closes it
   * when `autoClose` is enabled.
   */
  const closeMenu = useCallback(() => {
    if (!autoClose) {
      return;
    }

    handleOpenChange(false);
  }, [autoClose, handleOpenChange]);

  const handlePointerDownInside = useCallback(() => {
    hasPointerPressInsideRef.current = true;
  }, []);

  /*
   * Also closes on Escape directly, rather than relying only on Radix's own dismissable-layer
   * listener: that listener arms on just the most-recently-mounted layer app-wide, and this content
   * stays mounted forever once opened, so a second menu opened anywhere on the page permanently steals
   * the first menu's Escape handling. Radix's own dismiss only calls `preventDefault()`, so the event
   * still bubbles here when this menu *is* the highest layer too -- the equality guard in
   * `handleOpenChange` absorbs that duplicate close.
   */
  const handleKeyDownInside = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      hasPointerPressInsideRef.current = false;

      if (event.key === 'Escape') {
        handleOpenChange(false);
      }
    },
    [handleOpenChange]
  );

  /*
   * Radix moves focus into the dropdown from its focus scope's MOUNT effect, and the content stays
   * mounted from the first open onward -- so a reopen runs no mount effect and focus would be left on
   * the trigger with the arrow keys dead. Doing it here is what that effect would have done. The first
   * open is left to Radix, which puts focus on the first item rather than the content.
   */
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (!hasFocusedOnOpenRef.current) {
      hasFocusedOnOpenRef.current = true;

      return;
    }

    menuItemsRef.current?.focus();
  }, [isOpen]);

  /*
   * Escape never reaches `onKeyDown` in time to clear the flag: Radix dismisses from its own document
   * listener, which runs before the bubbling handler above. Without this, an earlier pointer press on
   * a child that did not close the menu -- an `autoClose={false}` toggle -- makes the Escape that does
   * close it look like a pointer close, and focus is left stranded on the now-hidden child.
   */
  const handleEscapeKeyDown = useCallback(() => {
    hasPointerPressInsideRef.current = false;
  }, []);

  const menuContextValue = useMemo(() => ({ autoClose, isInRadixMenu: hasOpened }), [autoClose, hasOpened]);

  /*
   * Hide the trigger for a menu that would open empty, so a caller can hand over a permission-gated
   * action list without writing a guard of its own. Counting the rendered children keeps that working
   * for any child type, including a section's own items component. In portal mode the items are not
   * mounted until the first open, so there is nothing to measure and the trigger always shows.
   */
  const showMenuButton = menuItemsCount === undefined || menuItemsCount > 0;

  useLayoutEffect(() => {
    if (portal) {
      return;
    }

    /*
     * A child that renders nothing still counts as a child, so the DOM is the only honest measure --
     * except when there is nothing renderable at all, where there is no container to measure.
     */
    const hasRenderableChildren = Children.toArray(props.children).some(Boolean);

    setMenuItemsCount(hasRenderableChildren ? menuItemsRef.current?.childElementCount : 0);
  }, [portal, props.children]);

  const renderedCustomIcon = isFunction(customIcon) ? customIcon(isOpen) : customIcon;
  let menuButton;

  switch (type) {
    case 'custom': {
      menuButton = (
        <CustomTrigger aria-label={ariaLabel} disabled={disabled} type="button">
          {renderedCustomIcon}
        </CustomTrigger>
      );
      break;
    }

    case 'default': {
      menuButton = (
        <Button
          disabled={disabled}
          icon={isOpen ? ChevronUpIcon : ChevronDownIcon}
          iconPosition="trailing"
          size={buttonSize}
          variant={buttonVariant}
        >
          {text}
        </Button>
      );
      break;
    }

    case 'more': {
      menuButton = (
        <IconButton aria-label={ariaLabel} iconButtonCss={iconButtonCss} size={buttonSize} variant={buttonVariant}>
          <MoreIcon />
        </IconButton>
      );
      break;
    }

    case 'more-vertical': {
      menuButton = (
        <IconButton aria-label={ariaLabel} iconButtonCss={iconButtonCss} size={buttonSize} variant={buttonVariant}>
          <MoreVerticalIcon />
        </IconButton>
      );
      break;
    }

    default: {
      break;
    }
  }

  const dropdown = (
    <MenuItemsContainer
      $matchTriggerWidth={matchTriggerWidth}
      align={MENU_PLACEMENT[position].align}
      collisionPadding={MENU_OFFSET}
      data-testid={ElementTestId.MENU_ITEMS_CONTAINER}
      // TODO: (ED-20531) forceMount keeps position tracking (autoUpdate) running on every closed menu forever
      forceMount
      loop
      onClick={closeMenu}
      onEscapeKeyDown={handleEscapeKeyDown}
      onInteractOutside={handleInteractOutside}
      onKeyDown={handleKeyDownInside}
      onPointerDown={handlePointerDownInside}
      ref={menuItemsRef}
      side={MENU_PLACEMENT[position].side}
      sideOffset={MENU_PLACEMENT[position].sideOffset}
    >
      {props.children}
    </MenuItemsContainer>
  );

  return (
    <Container $noZIndex={noZIndex} $showMenuButton={showMenuButton} data-testid={testId}>
      <MenuContext.Provider value={menuContextValue}>
        {/* `modal={false}`: the default traps focus and marks outside content aria-hidden, which would
         * make a dialog opened from a menu item inert. */}
        <RadixDropdownMenu.Root modal={false} onOpenChange={handleOpenChange} open={isOpen}>
          <MenuIconContainer>
            <RadixDropdownMenu.Trigger asChild disabled={disabled} ref={triggerRef}>
              {menuButton}
            </RadixDropdownMenu.Trigger>
          </MenuIconContainer>
          {hasOpened && portal ? (
            <RadixDropdownMenu.Portal container={portalContainer} forceMount>
              {dropdown}
            </RadixDropdownMenu.Portal>
          ) : null}
          {hasOpened && !portal ? dropdown : null}
          {/* Before the first open, and again once the Radix content has unmounted after a close, this
           * legacy path renders the items hidden, so an empty menu can hide its trigger without the
           * user having to open it. Portal mode mounts nothing until the first open, which is what
           * keeps a menu item's own effects and queries from running early.
           *
           * Swapping this for the Radix content on open remounts the children, same as it does the
           * first time. Keeping a force-mounted content here instead is worse: its focus scope puts
           * guards in the tab order from first paint, so Tab no longer reaches the trigger. */}
          {!hasOpened && !portal && (
            <ClosedMenuItemsContainer data-testid={ElementTestId.MENU_ITEMS_CONTAINER} ref={menuItemsRef}>
              {props.children}
            </ClosedMenuItemsContainer>
          )}
        </RadixDropdownMenu.Root>
      </MenuContext.Provider>
    </Container>
  );
};

export default MenuButton;
